import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { useHistory } from "react-router-dom";

import classnames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import type { DeliveryOption } from "@jti/commerce";
import { getEmptyAddress, InStoreDeliveryStoreSelector } from "@jti/commerce";
import { useCheckoutConfiguration } from "@jti/commerce/providers";
import type { ExternalShippingCarrier } from "@jti/commerce-types";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Button } from "@jti/core-components/components/Button";
import { Separator } from "@jti/core-components/components/Separator";
import { toast } from "@jti/core-components/components/Toaster";
import { newrelic } from "@jti/fe-clients";
import type { Address, FlotiqInStoreDeliveryStore } from "@jti/fe-common";
import { CheckoutRoutes, getErrorMessageFromError, isAddressFormExtendedWithProvince } from "@jti/fe-common";
import { getCurrentMagentoClient } from "@jti/magento";
import { MagentoGraphQLRequestClientError } from "@jti/magento-api-v2";
import {
  CasAddressesApi,
  CasProfileApi,
  useGetProfile,
  usePlatformValidation,
  useUpdateCustomAttributes,
} from "@jti/rest-api";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectAccountContact } from "@jti/store/selectors/selectAccountContact";
import { selectAddresses } from "@jti/store/selectors/selectAddresses";
import { selectAddressesFetchState } from "@jti/store/selectors/selectAddressesFetchState";
import { selectDefaultBillingAddress } from "@jti/store/selectors/selectDefaultBillingAddress";
import { selectDefaultShippingAddress } from "@jti/store/selectors/selectDefaultShippingAddress";
import { selectProductsInCart } from "@jti/store/selectors/selectProductsInCart";
import { selectShippingAddressSelected } from "@jti/store/selectors/selectShippingAddressSelected";
import {
  createAddress as createAddressAction,
  DeliveryOptionType,
  setShippingSelectedAddressId,
  updateOrderAttributes,
} from "@jti/store/slices/LEGACY__checkoutSlice";
import { type CheckoutShippingStepData, setShippingStepData } from "@jti/store/slices/standardCheckoutFormSlice";
import { Form, getCountriesCodesArray, useForm, useTranslations } from "@jti/ui";

import { DeliveryOptions } from "@checkout/components/DeliveryOptions";
import {
  CustomDeliveryOption,
  validateStoreAddress,
} from "@checkout/components/DeliveryOptions/CustomDeliveryOption/CustomDeliveryOption";
import { OrderAttributes } from "@checkout/components/OrderAttributes";
import { useAgeVerification } from "@checkout/hooks/useAgeVerification";
import { useDeliveryLimit } from "@checkout/hooks/useDeliveryLimit";
import { useFreeTrial } from "@checkout/hooks/useFreeTrial";
import { useLoyalty } from "@checkout/hooks/useLoyalty";
import useShipping from "@checkout/hooks/useShipping";
import { useTrialBlockingCheckout } from "@checkout/hooks/useTrialBlockingCheckout";
import { DeliveryLimitMessageBox } from "@checkout/pages/ShippingPage/components/DeliveryLimitMessageBox";
import type { PhoneNumber } from "@checkout/types";

import { BillingAddress } from "./components/BillingAddress";
import { ShippingAddress } from "./components/ShippingAddress";
import { ShippingContact } from "./components/ShippingContact";
import { getCurrentDeliveryOptionType, getDeliveryOptionType } from "./utils/getCurrentDeliveryOptionType";
import { getInitialDeliveryOption } from "./utils/getInitialDeliveryOption";
import { getSelectedShippingAddress } from "./utils/getSelectedShippingAddress";
import { isExternalAddressValidationGraphqlError } from "./utils/isExternalAddressValidationGraphqlError/isExternalAddressValidationGraphqlError";
import { propagateContactDataToAddress } from "./utils/propagateContactDataToAddress";
import { transformDeliveryOptionToShippingMethod } from "./utils/transformDeliveryOptionToShippingMethod";

import { SHIPPING_FORM_FIELDS } from "./constants";
import { defaultShippingContact } from "./defaultShippingFormValues";

import styles from "./ShippingForm.module.scss";

type ShippingFormProps = {
  asideSubmitButtonWrapperRef: React.RefObject<HTMLDivElement | null>;
};

type AddressExternalValidationErrorType = "shipping-address" | "billing-address" | null;

type UpdateCheckoutAddressParams = {
  address: Address;
  formField: typeof SHIPPING_FORM_FIELDS.shippingAddress | typeof SHIPPING_FORM_FIELDS.billingAddress;
  validationErrorType: Exclude<AddressExternalValidationErrorType, null>;
  errorMessageFallback: string;
  mutation: (
    magentoClient: ReturnType<typeof getCurrentMagentoClient>,
    cartIdValue: string,
    addressValue: Address
  ) => Promise<unknown>;
};

export const ShippingForm: FC<ShippingFormProps> = ({ asideSubmitButtonWrapperRef }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { __, dictionary } = useTranslations();
  const config = useConfig();
  const checkoutConfiguration = useCheckoutConfiguration();
  const formRef = useRef<HTMLFormElement>(null);
  const { data: profileData } = useGetProfile();

  const { isFreeTrial } = useFreeTrial();
  const { isAgeVerificationError } = useAgeVerification();
  const { isTrialBlockingCheckout } = useTrialBlockingCheckout();
  const { hasCampaignSelected } = useLoyalty();

  const { cartId = "", orderDeliveryLimits } = useShipping();

  const accountContact = useSelector(selectAccountContact);
  const cartState = useSelector((state) => state.cart);
  const country = useSelector((state) => state.checkout.country);
  const productsInCart = useSelector((state) =>
    selectProductsInCart(state, isFreeTrial, config.showPricesWithTaxExcluded)
  );
  const addressesFetchState = useSelector((state) => selectAddressesFetchState(state));
  const shippingAddressSelected = useSelector((state) => selectShippingAddressSelected(state, country));
  const savedDefaultBillingAddress = useSelector((state) => selectDefaultBillingAddress(state, country));
  const addresses = useSelector(selectAddresses);
  const savedDefaultShippingAddress = useSelector((state) => selectDefaultShippingAddress(state));
  const { shippingStepData } = useSelector((state) => state.standardCheckoutForm);
  const { updateCustomAttributes } = useUpdateCustomAttributes();
  const { validatePhone } = usePlatformValidation();

  const isAddressExtendedWithProvince = isAddressFormExtendedWithProvince(config.addressFormVariant);

  const defaultDeliveryMethod = useMemo(() => {
    return {
      description: "",
      label: __("cart.checkout.delivery.at_home"),
      methodCode: DeliveryOptionType.AtHome,
      widgetTagName: "at-home",
      defaultDelivery: false,
      lat: "",
      lon: "",
      flotiqApiKey: "",
    };
  }, [__]);

  const deliveryOptions: DeliveryOption[] = useMemo(
    () =>
      checkoutConfiguration.deliveryOptions.length ? checkoutConfiguration.deliveryOptions : [defaultDeliveryMethod],
    [checkoutConfiguration.deliveryOptions, defaultDeliveryMethod]
  );
  const isEditingShippingAddress = !shippingAddressSelected?.id;

  const initialDeliveryOption = useMemo(() => {
    return getInitialDeliveryOption(deliveryOptions);
  }, [deliveryOptions]);

  const countriesCodesArray = useMemo(() => getCountriesCodesArray(config), [config]);

  const isAccountPhoneNumberInvalid = !!accountContact?.telephone && !isPossiblePhoneNumber(accountContact?.telephone);

  const _hiddenValues = isAddressExtendedWithProvince
    ? { immutableProvinceCode: profileData?.extra?.province ?? "" }
    : undefined;

  const form = useForm<CheckoutShippingStepData>({
    mode: "onChange" as const,
    reValidateMode: "onChange" as const,
    defaultValues: shippingStepData ?? {
      shippingAddress: undefined, // this should not be in the form. Selected shipping address should be defined based on delivery option on form submit
      atHomeDeliveryShippingAddress: savedDefaultShippingAddress,
      shouldUseAccountContact: !isAccountPhoneNumberInvalid,
      shippingContact: defaultShippingContact,
      billingAddressType: "shipping",
      billingAddress: getEmptyAddress<Address>({ overrides: country }),
      deliveryOptionType: getDeliveryOptionType(initialDeliveryOption),
      orderAttributes: {},
      shippingMethodCode: initialDeliveryOption.methodCode,
      shouldUseInStoreDeliveryBillingAddress: false,
      externalShippingCarrier: null,
      editPhone: {
        code: countriesCodesArray[0] || "",
        phone: "",
      },
      ...(_hiddenValues && { _hiddenValues }),
    },
    shouldUnregister: false,
  });

  const { errors: formErrors, isSubmitting: isFormSubmitting } = form.formState;
  const shouldUseAccountContact = form.watch("shouldUseAccountContact");
  const shippingContactInput = form.watch("shippingContact");
  const shippingMethodCode = form.watch(SHIPPING_FORM_FIELDS.shippingMethodCode);
  const atHomeDeliveryShippingAddressValue = form.watch(SHIPPING_FORM_FIELDS.atHomeDeliveryShippingAddress);
  const billingAddressTypeValue = form.watch(SHIPPING_FORM_FIELDS.billingAddressType);
  const inStoreDeliveryStoreValue = form.watch("inStoreDeliveryStore");
  const deliveryOptionTypeValue = form.watch("deliveryOptionType");
  const externalShippingCarrierValue = form.watch("externalShippingCarrier");
  const { isDeliveryLimitError } = useDeliveryLimit({
    enabled: deliveryOptionTypeValue === DeliveryOptionType.AtHome,
  });

  const [updatedPhoneNumber, setUpdatedPhoneNumber] = useState<PhoneNumber | null>(null);
  const [addressExternalValidationError, setAddressExternalValidationError] =
    useState<AddressExternalValidationErrorType>(null);

  const isExternalValidationShippingAddressErrorVisible = addressExternalValidationError === "shipping-address";

  const { allow_empty_firstname } = cartState.storeConfig;

  const initialContactData = useMemo(() => {
    const telephone = shippingContactInput?.telephone || accountContact?.telephone;
    const firstname = allow_empty_firstname ? "" : accountContact?.firstname;

    const newShippingContact = {
      ...accountContact,
      telephone: telephone ?? "",
      firstname: firstname ?? "",
      lastname: accountContact?.lastname ?? "",
    };

    return newShippingContact;
  }, [accountContact, shippingContactInput?.telephone, allow_empty_firstname]);

  const contactInformation = shouldUseAccountContact ? initialContactData : shippingContactInput;

  const isPhoneValidationError = useMemo(() => {
    if (/true/i.test(config.CHECKOUT_PHONE_REQUIRED) && shouldUseAccountContact) {
      return !contactInformation?.telephone && !accountContact?.telephone;
    }

    return false;
  }, [
    shouldUseAccountContact,
    contactInformation?.telephone,
    accountContact?.telephone,
    config.CHECKOUT_PHONE_REQUIRED,
  ]);

  const isValidExternalAddress = useMemo(() => {
    if (deliveryOptionTypeValue !== DeliveryOptionType.CustomDeliveryOption) {
      return true;
    }

    return Boolean(
      !!externalShippingCarrierValue?.address && validateStoreAddress(externalShippingCarrierValue.address)
    );
  }, [deliveryOptionTypeValue, externalShippingCarrierValue]);

  const selectedDeliveryOption =
    deliveryOptions.find((option) => option.methodCode === shippingMethodCode) || defaultDeliveryMethod;

  const isSubmitEnabled =
    !!Object.keys(formErrors).length ||
    (deliveryOptionTypeValue === DeliveryOptionType.InStoreJti && inStoreDeliveryStoreValue == null) ||
    isDeliveryLimitError ||
    isAgeVerificationError ||
    isTrialBlockingCheckout ||
    !isValidExternalAddress ||
    !hasCampaignSelected;

  const getBillingAddressForNonHomeDelivery = (
    formData: Pick<
      CheckoutShippingStepData,
      typeof SHIPPING_FORM_FIELDS.billingAddress | "shouldUseInStoreDeliveryBillingAddress"
    >
  ) => {
    const shouldUseDefaultBillingAddress =
      savedDefaultBillingAddress && !formData.shouldUseInStoreDeliveryBillingAddress;

    const billingAddress = shouldUseDefaultBillingAddress
      ? { ...savedDefaultBillingAddress }
      : { ...formData.billingAddress, ...contactInformation };

    return billingAddress;
  };

  const getSelectedBillingAddress = (selectedShippingAddress: Address, formData: CheckoutShippingStepData) => {
    const isAtHomeDelivery = formData.deliveryOptionType === DeliveryOptionType.AtHome;

    if (isAtHomeDelivery && formData.billingAddressType === "shipping") {
      return structuredClone(selectedShippingAddress);
    }

    if (isAtHomeDelivery && formData.billingAddressType !== "shipping") {
      return structuredClone({ ...formData.billingAddress, ...contactInformation });
    }

    if (!config.isBillingAddressVisibleForCheckout) {
      return structuredClone(selectedShippingAddress);
    }

    if (!isAtHomeDelivery) {
      return getBillingAddressForNonHomeDelivery(formData);
    }

    return structuredClone(formData.billingAddress);
  };

  const onShippingContactEdit = (countryCode: string, number: string, phoneVerificationUuid: string) => {
    setUpdatedPhoneNumber({ countryCode, number, phoneVerificationUuid });
  };

  const createAddress = async () => {
    const isDefault = addresses.length === 0;

    try {
      await dispatch(
        createAddressAction({
          address: {
            ...atHomeDeliveryShippingAddressValue,
            ...accountContact,
            isDefaultBilling: isDefault,
            isDefaultShipping: isDefault,
          },
        })
      );
    } catch (error) {
      // exit silently
      newrelic.noticeError(new Error(`CAS_CREATE_ADDRESS_ERROR: ${error}`));
    }
  };

  const setInStoreDeliveryStore = (store: FlotiqInStoreDeliveryStore) => {
    form.setValue("inStoreDeliveryStore", store);
  };

  const setExternalShippingCarrier = (carrierData: ExternalShippingCarrier) => {
    form.setValue("externalShippingCarrier", carrierData);
  };

  const handleMobileSubmit = async () => {
    const phoneField = config.isInternationalPhoneEnabled ? "editPhone.internationalPhone" : "editPhone.phone";
    const formData = form.getValues();
    const { number = "", countryCode = "" } = (updatedPhoneNumber as PhoneNumber) || {};

    const internationalPhoneNumber = config.isInternationalPhoneEnabled
      ? formData.editPhone?.internationalPhone
      : undefined;

    const fullPhoneNumber = internationalPhoneNumber || `${countryCode}${number}`;

    const isPhoneNumberFilledIn = config.isInternationalPhoneEnabled ? !!internationalPhoneNumber : !!number;

    if (isPhoneNumberFilledIn) {
      const validatePhoneResponse = await validatePhone(fullPhoneNumber);

      const isValidPhone = validatePhoneResponse?.is_valid;

      if (!isValidPhone) {
        form.setError(phoneField, {
          type: "server",
          message: __("form_validation.phone_number_pattern"),
        });
        form.setFocus(phoneField);
        return;
      }
    }

    formRef.current?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
  };

  const updateCheckoutAddress = async ({
    address,
    formField,
    validationErrorType,
    errorMessageFallback,
    mutation,
  }: UpdateCheckoutAddressParams) => {
    const magentoClient = getCurrentMagentoClient(isFreeTrial);

    try {
      await mutation(magentoClient, cartId, address);
    } catch (err) {
      if (!MagentoGraphQLRequestClientError.isMagentoGraphQLRequestClientError(err)) {
        newrelic.noticeError(new Error(`${err}`));
        throw err;
      }

      if (isExternalAddressValidationGraphqlError(err)) {
        setAddressExternalValidationError(validationErrorType);
        window.scrollTo({ top: 0, behavior: "smooth" });
        throw err;
      }

      const errorMessage = err?.response?.errors?.[0].message || errorMessageFallback;

      form.setError(`root.${formField}`, {
        type: "server",
        message: errorMessage,
      });
      toast.error(errorMessage);
      throw err;
    }
  };

  const updateAddressBeforeSubmit = (address: Address) => {
    if (isAddressExtendedWithProvince) {
      address.regionCode = profileData?.extra?.province;
    }

    return address;
  };

  const handleSubmit = async (data: CheckoutShippingStepData) => {
    const fiscalCode = data.orderAttributes?.fiscal_code?.trim();

    if (typeof fiscalCode === "string" && !!fiscalCode) {
      try {
        await updateCustomAttributes({ fiscal_code: fiscalCode });
      } catch (error) {
        const errorMessage = getErrorMessageFromError(
          error,
          __("cart.checkout.error.incorrect_fiscal_code"),
          dictionary
        );
        form.setError("orderAttributes.fiscal_code", {
          type: "server",
          message: errorMessage,
        });
        form.setFocus("orderAttributes.fiscal_code");
        return;
      }
    }

    const { number = "", countryCode = "", phoneVerificationUuid } = (updatedPhoneNumber as PhoneNumber) || {};
    const newPhoneNumber = number && countryCode ? `${countryCode}${number}` : "";
    const selectedShippingContact = structuredClone(contactInformation);
    const selectedShippingAddress = getSelectedShippingAddress(data);

    if (!selectedShippingAddress) {
      toast.error(__("cart.checkout.warning.problem_with_shipping_address"));
      return;
    }
    const selectedShippingAddressWithContact = propagateContactDataToAddress(
      selectedShippingAddress,
      selectedShippingContact
    );
    const internationalPhoneNumber = config.isInternationalPhoneEnabled
      ? data.editPhone?.internationalPhone
      : undefined;

    selectedShippingAddressWithContact.telephone =
      internationalPhoneNumber || newPhoneNumber || selectedShippingContact?.telephone || "";

    const fullPhoneNumber = internationalPhoneNumber || `${countryCode}${number}`;
    const phoneField = config.isInternationalPhoneEnabled ? "editPhone.internationalPhone" : "editPhone.phone";

    const isPhoneNumberFilledIn = config.isInternationalPhoneEnabled ? !!internationalPhoneNumber : !!number;

    if (isPhoneNumberFilledIn) {
      const validatePhoneResponse = await validatePhone(fullPhoneNumber);

      const isValidPhone = validatePhoneResponse?.is_valid;

      if (!isValidPhone) {
        form.setError(phoneField, {
          type: "server",
          message: __("form_validation.phone_number_pattern"),
        });
        form.setFocus(phoneField);
        return;
      }
    }

    if (isPhoneValidationError) {
      try {
        await CasProfileApi.init().updatePhone(fullPhoneNumber, phoneVerificationUuid, "");
        if (shouldUseAccountContact && shippingAddressSelected?.id && fullPhoneNumber) {
          // this request is not awaited because it's not critical for the checkout process and it could prolong the process
          CasAddressesApi.init().updateAddress(shippingAddressSelected.id, {
            ...shippingAddressSelected,
            telephone: fullPhoneNumber,
          });
        }
        if (selectedShippingContact) {
          selectedShippingContact.telephone = fullPhoneNumber;
        }
        selectedShippingAddressWithContact.telephone = fullPhoneNumber;
      } catch (err: any) {
        toast.error(`${err?.response?.data?.message || __("cart.checkout.warning.problem_with_phone_number_update")}`);
        return;
      }
    }
    const selectedBillingAddress = getSelectedBillingAddress(selectedShippingAddressWithContact, data);

    try {
      await updateCheckoutAddress({
        address: selectedShippingAddressWithContact,
        formField: SHIPPING_FORM_FIELDS.shippingAddress,
        validationErrorType: "shipping-address",
        errorMessageFallback: __("cart.checkout.warning.problem_with_shipping_address"),
        mutation: (magentoClient, cartIdValue, addressValue) =>
          magentoClient.setShippingAddressOnCart(cartIdValue, updateAddressBeforeSubmit(addressValue)),
      });
      await updateCheckoutAddress({
        address: selectedBillingAddress,
        formField: SHIPPING_FORM_FIELDS.billingAddress,
        validationErrorType: "billing-address",
        errorMessageFallback: __("cart.checkout.warning.problem_with_billing_address"),
        mutation: (magentoClient, cartIdValue, addressValue) =>
          magentoClient.setBillingAddressOnCart(cartIdValue, updateAddressBeforeSubmit(addressValue)),
      });
    } catch {
      return;
    }

    const createAddressPromise =
      isEditingShippingAddress && data.deliveryOptionType === DeliveryOptionType.AtHome ? createAddress() : undefined;
    if (data.orderAttributes) {
      dispatch(updateOrderAttributes(data.orderAttributes));
    }
    dispatch(
      setShippingStepData({
        ...data,
        shippingAddress: selectedShippingAddressWithContact,
        shippingMethodCode: data.shippingMethodCode,
        deliveryOptionType: data.deliveryOptionType,
      })
    );
    const deliveryPageUrl = new CheckoutRoutes({ isFreeTrial }).delivery;
    if (createAddressPromise) {
      await createAddressPromise;
    }
    history.push(deliveryPageUrl);
  };

  useEffect(() => {
    if (addressesFetchState.isSuccess && addresses.length > 0) {
      dispatch(setShippingSelectedAddressId());
    }
  }, [dispatch, addressesFetchState.status]);

  useEffect(
    function onDeliveryOptionChange() {
      form.clearErrors();

      const newDeliveryOptionType = getCurrentDeliveryOptionType(deliveryOptions, shippingMethodCode);

      form.setValue("deliveryOptionType", newDeliveryOptionType);

      if (newDeliveryOptionType !== DeliveryOptionType.AtHome) {
        form.unregister(SHIPPING_FORM_FIELDS.atHomeDeliveryShippingAddress, { keepValue: true });
      }
    },
    [shippingMethodCode, deliveryOptions]
  );

  useEffect(
    function resetErrorOnBillingAddressTypeChange() {
      form.clearErrors("billingAddress");
    },
    [form.clearErrors, billingAddressTypeValue]
  );

  useEffect(
    function handleMagentoAddressExternalValidationErrorClearing() {
      const subscription = form.watch((_value, { name }) => {
        if (!name) return;

        if (
          name.startsWith(SHIPPING_FORM_FIELDS.atHomeDeliveryShippingAddress) &&
          addressExternalValidationError === "shipping-address"
        ) {
          setAddressExternalValidationError(null);
          return;
        }

        if (
          name.startsWith(SHIPPING_FORM_FIELDS.billingAddress) &&
          addressExternalValidationError === "billing-address"
        ) {
          setAddressExternalValidationError(null);
          return;
        }

        if (name === SHIPPING_FORM_FIELDS.billingAddressType && addressExternalValidationError === "billing-address") {
          setAddressExternalValidationError(null);
          return;
        }

        if (name === SHIPPING_FORM_FIELDS.shippingMethodCode && !!addressExternalValidationError) {
          setAddressExternalValidationError(null);
          return;
        }
      });
      return () => subscription.unsubscribe();
    },
    [form.watch, addressExternalValidationError]
  );

  const shouldHideDeliveryOptionsSeparator = deliveryOptions.length === 1 && deliveryOptions[0].methodCode === "atHome";

  return (
    <Form<CheckoutShippingStepData>
      dataTestId="shippingForm"
      form={form}
      onSubmit={handleSubmit}
      className={styles.container}
      ref={formRef}
    >
      <DeliveryLimitMessageBox isVisible={isDeliveryLimitError} />

      {!!addressExternalValidationError && (
        <AlertBanner
          data-testid="info-banner-wrapper"
          tone="warning"
          description={
            addressExternalValidationError === "billing-address"
              ? __("cart.checkout.warning.billing_address_validation_description")
              : __("cart.checkout.warning.shipping_address_validation_description")
          }
        />
      )}
      <ShippingContact
        initialContactData={initialContactData}
        onEdit={onShippingContactEdit}
        isPhoneValidationErrorVisible={isPhoneValidationError}
        isAccountPhoneNumberInvalid={isAccountPhoneNumberInvalid}
      >
        {shouldHideDeliveryOptionsSeparator ? null : (
          <div className={styles.separator}>
            <Separator data-testid="separator" />
          </div>
        )}

        <div className={styles.deliveryAddressSelectorWrapper}>
          {deliveryOptions.length > 1 && (
            <DeliveryOptions options={deliveryOptions} name={SHIPPING_FORM_FIELDS.shippingMethodCode} />
          )}

          {deliveryOptionTypeValue === DeliveryOptionType.CustomDeliveryOption &&
            selectedDeliveryOption.widgetTagName && (
              <CustomDeliveryOption
                {...selectedDeliveryOption}
                key={shippingMethodCode}
                onSelectAddress={setExternalShippingCarrier}
              />
            )}
          {deliveryOptionTypeValue === DeliveryOptionType.InStoreJti && (
            <InStoreDeliveryStoreSelector
              key={shippingMethodCode}
              productsInCart={productsInCart}
              onStoreSelect={setInStoreDeliveryStore}
              selectedStore={inStoreDeliveryStoreValue}
              description={selectedDeliveryOption.description}
              storeLocatorConfiguration={transformDeliveryOptionToShippingMethod(selectedDeliveryOption)}
            />
          )}
        </div>
      </ShippingContact>
      {deliveryOptionTypeValue === DeliveryOptionType.AtHome && (
        <ShippingAddress
          orderDeliveryLimits={orderDeliveryLimits}
          isExternalValidationShippingAddressErrorVisible={isExternalValidationShippingAddressErrorVisible}
        />
      )}
      {config.isBillingAddressVisibleForCheckout && (
        <BillingAddress
          billingAddressType={billingAddressTypeValue}
          deliveryOptionType={deliveryOptionTypeValue}
          errorMessage={formErrors.root?.billingAddress?.message}
        />
      )}
      <div>
        <OrderAttributes target={"buttonDelivery_before"} />
      </div>

      {/* Desktop button */}
      <div
        className={classnames([styles.submitButtonWrapper, styles.desktop])}
        data-testid="shipping-form-submit-button-wrapper-desktop"
      >
        <Button pending={isFormSubmitting} disabled={isSubmitEnabled} type="submit">
          {__("cart.checkout.next_delivery.button")}
        </Button>
      </div>

      {/* Mobile button */}
      {asideSubmitButtonWrapperRef.current &&
        createPortal(
          <div
            className={classnames([styles.submitButtonWrapper, styles.mobile])}
            data-testid="shipping-form-submit-button-wrapper-mobile"
          >
            <Button disabled={isSubmitEnabled} pending={isFormSubmitting} type="button" onClick={handleMobileSubmit}>
              {__("cart.checkout.next_delivery.button")}
            </Button>
          </div>,
          asideSubmitButtonWrapperRef.current
        )}
    </Form>
  );
};
